import { Input, Spin, Button } from "antd";
import { LoadingOutlined } from '@ant-design/icons';
import { memo, useEffect, useRef, useState } from "react"
import styled from "styled-components";
import { UI_Header, UI_HeaderLeft, UI_HeaderRight, UI_HeaderRight_item } from "../PanelStyle";
import { useAtom } from 'jotai';
import { showSearchAddressPanelAtom } from "../../stores/mapStore";
import { FaXmark } from "react-icons/fa6";
import { PiMagnifyingGlassBold } from "react-icons/pi";
import { Map_ForwardGeocoding_Request, Map_ForwardGeocoding_ResponseItem, MapAPIs } from "~/common/typescript/apis/MapAPIs";
import AddressItem from "./AddressItem";
import MapUtils from "../../utils/MapUtils";

const COORD_REGEX = /^\s*(-?\d+\.?\d*)\s*[,;]\s*(-?\d+\.?\d*)\s*$/;

const parseCoordinate = (input: string): { lat: number; lng: number } | null => {
  const match = input.match(COORD_REGEX);
  if (match) {
    const lat = parseFloat(match[1]);
    const lng = parseFloat(match[2]);
    if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
      return { lat, lng };
    }
  }
  return null;
};

export const SearchAddressPanel = () => {
  const [showSearchAddressPanel, setShowSearchAddressPanel] = useAtom(showSearchAddressPanelAtom);

  const [keyword, setKeyword] = useState<string>("");
  const [debouncedKeyword, setDebouncedKeyword] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [addressList, setAddressList] = useState<Map_ForwardGeocoding_ResponseItem[]>([]);
  const inputRef = useRef<typeof Input>(null);

  const coordinate = keyword ? parseCoordinate(keyword) : null;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedKeyword(keyword);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [keyword]);

  useEffect(() => {
    if (debouncedKeyword && !coordinate) {
      console.log("Searching for address: ", debouncedKeyword);
      setIsLoading(true);

      const request: Map_ForwardGeocoding_Request = {
        address: debouncedKeyword,
      }
      MapAPIs.remote_forwardGeocoding(request).then((response) => {
        console.log("Address search response: ", response);
        setAddressList(response.results);
      }).catch((error) => {
        console.error("Error searching address: ", error);
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [debouncedKeyword, coordinate]);

  const handleGoToCoordinate = () => {
    if (coordinate) {
      MapUtils.zoomToFeatureCollection({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [coordinate.lng, coordinate.lat],
            },
            properties: {},
          },
        ]
      });
      setShowSearchAddressPanel(false);
    }
  };

  return <UI_Wrapper>
    {/* Header */}
    <UI_Header>
      <UI_HeaderLeft style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <PiMagnifyingGlassBold style={{ color: '#2563eb', fontSize: '18px' }} />
        <span>Tìm kiếm địa chỉ</span>
      </UI_HeaderLeft>
      <UI_HeaderRight>
        <UI_HeaderRight_item onClick={() => {
          setShowSearchAddressPanel(false);
        }}>
          <FaXmark />
        </UI_HeaderRight_item>
      </UI_HeaderRight>
    </UI_Header>
    {/* End Header */}
    {/* Content */}

    <UI_Content>
      <div style={{ marginBottom: 16 }}>
        <div>
          <Input
            ref={inputRef}
            placeholder={"Nhập địa chỉ hoặc tọa độ lat, lng"} value={keyword}
            prefix={isLoading ? <Spin indicator={<LoadingOutlined spin />} /> : <span />}
            onChange={(e) => {
              setKeyword(e.target.value);
            }} />
        </div>
      </div>

      {coordinate ? (
        <div>
          <div style={{ marginBottom: 8, fontSize: 12, color: '#aaa' }}>Tọa độ: {coordinate.lat}, {coordinate.lng}</div>
          <Button type="primary" block onClick={handleGoToCoordinate}>
            Phóng tới địa điểm này
          </Button>
        </div>
      ) : debouncedKeyword && <div>
        <div style={{ marginBottom: 8, fontSize: 12, color: '#aaa' }}>Kết quả tìm kiếm</div>
        <UI_AddressList>
          {
            addressList.map((address, index) => {
              return <AddressItem key={index} address={address} />
            })
          }
          {
            addressList.length === 0 && !isLoading && <div>Không tìm thấy địa chỉ nào</div>
          }
        </UI_AddressList>
      </div>
      }
    </UI_Content>
  </UI_Wrapper>
}

export default memo(SearchAddressPanel);

const UI_Wrapper = styled.div`
  width: 300px;
  height: 500px;
  position: absolute;
  top: 16px;
  right: 16px;
  background-color: white;
  border-radius: 8px;
  background-color: rgba(255,255,255, 0.8);
  backdrop-filter: blur(10px);
  box-shadow: var(--shadows-paneShadow);
`;

const UI_Content = styled.div`
  padding: 16px;
`

const UI_AddressList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  max-height: 300px;
`;
